import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, CircleAlert, Database, FileUp, Info, Upload } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import {
  collectMachineTypeValues,
  collectSalutationCodes,
  decodeCsv,
  parseCsv,
  planCustomerImport,
  planMachineImport,
  suggestTypeMapping,
  type CsvTable,
  type CustomerDraft,
  type CustomerKind,
  type DecodedCsv,
  type ImportContext,
  type ImportGateway,
  type ImportPlan,
  type MachineDraft,
  type PlannedRow,
} from '@forq/import-data';
import { SearchSelect } from '@forq/ui';

type ImportKind = 'customers' | 'machines';

interface ImportPageProps {
  readonly tenant: TenantSummary;
  readonly gateway: ImportGateway;
}

const actionLabels = {
  create: 'Neu',
  update: 'Aktualisieren',
  unchanged: 'Unverändert',
  blocked: 'Blockiert',
} as const;

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Die Übernahme ist fehlgeschlagen.';
}

export function ImportPage({ tenant, gateway }: ImportPageProps) {
  const [kind, setKind] = useState<ImportKind>('customers');
  const [context, setContext] = useState<ImportContext | null>(null);
  const [file, setFile] = useState<{ readonly name: string; readonly decoded: DecodedCsv; readonly table: CsvTable } | null>(null);
  const [salutationKinds, setSalutationKinds] = useState<Record<string, CustomerKind>>({});
  const [typeMapping, setTypeMapping] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    gateway.loadContext(tenant.id)
      .then((loaded) => { if (!cancelled) setContext(loaded); })
      .catch((cause: unknown) => { if (!cancelled) setError(messageOf(cause)); });
    return () => { cancelled = true; };
  }, [gateway, tenant.id]);

  // Mandantenwechsel oder Wechsel der Datenart: der alte Entwurf hat hier nichts mehr zu suchen.
  const reset = useCallback(() => {
    setFile(null);
    setSalutationKinds({});
    setTypeMapping({});
    setError(null);
    setResult(null);
  }, []);

  async function pickFile(picked: File): Promise<void> {
    setError(null);
    setResult(null);
    try {
      const decoded = decodeCsv(new Uint8Array(await picked.arrayBuffer()));
      const table = parseCsv(decoded.text);
      setFile({ name: picked.name, decoded, table });

      // Vorschlag, den der Anwender bestätigt -- nicht die Entscheidung selbst.
      if (kind === 'machines' && context) {
        setTypeMapping({ ...suggestTypeMapping(collectMachineTypeValues(table), context.machineTypes) });
      }
    } catch (cause: unknown) {
      setError(messageOf(cause));
    }
  }

  const plan: ImportPlan<CustomerDraft> | ImportPlan<MachineDraft> | null = useMemo(() => {
    if (!file || !context) return null;
    if (kind === 'customers') {
      return planCustomerImport(file.table, {
        salutationKinds,
        existing: context.customers.map((entry) => ({ number: entry.number, displayName: entry.name })),
      });
    }
    return planMachineImport(file.table, {
      types: context.machineTypes,
      typeMapping,
      customers: context.customers,
      existing: context.machineNumbers.map((number) => ({ number })),
    });
  }, [context, file, kind, salutationKinds, typeMapping]);

  const salutationCodes = useMemo(
    () => (file && kind === 'customers' ? collectSalutationCodes(file.table) : []),
    [file, kind],
  );
  const machineTypeValues = useMemo(
    () => (file && kind === 'machines' ? collectMachineTypeValues(file.table) : []),
    [file, kind],
  );

  async function run(): Promise<void> {
    if (!plan) return;
    const drafts = plan.rows.map((row) => row.draft).filter((draft): draft is CustomerDraft & MachineDraft => draft !== null);
    if (drafts.length === 0) return;

    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const outcome = kind === 'customers'
        ? await gateway.importCustomers(tenant.id, drafts as readonly CustomerDraft[])
        : await gateway.importMachines(tenant.id, drafts as readonly MachineDraft[]);
      setResult(`${outcome.created} neu angelegt, ${outcome.updated} aktualisiert.`);
      setContext(await gateway.loadContext(tenant.id));
    } catch (cause: unknown) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  const importable = plan ? plan.counts.create + plan.counts.update : 0;

  return (
    <section className="import-page">
      <header className="import-page__head">
        <div>
          <span className="forq-eyebrow"><Database size={11} /> Datenübernahme</span>
          <h1>Stammdaten aus C.O.S. Arista</h1>
          <p>
            CSV auswählen, Vorschau prüfen, übernehmen. Die Arista-Nummern werden übernommen –
            nicht neu vergeben, denn eure Konten laufen darauf. Ein zweiter Durchlauf derselben
            Datei aktualisiert, statt zu verdoppeln.
          </p>
        </div>
        <span className="import-page__tenant">{tenant.name}</span>
      </header>

      <div className="import-kinds" role="tablist">
        {(['customers', 'machines'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={kind === option}
            className={kind === option ? 'is-active' : undefined}
            onClick={() => { setKind(option); reset(); }}
          >
            {option === 'customers' ? 'Kunden' : 'Maschinen'}
          </button>
        ))}
      </div>

      <label className="import-drop">
        <FileUp size={20} />
        <span>
          <strong>{file ? file.name : `${kind === 'customers' ? 'Kundenadresse' : 'Maschinendaten'}.CSV auswählen`}</strong>
          <small>Aus Arista exportiert – Umlaute und Trennzeichen erkennt FORQ selbst.</small>
        </span>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(event) => {
            const picked = event.target.files?.[0];
            if (picked) void pickFile(picked);
          }}
        />
      </label>

      {file && (
        <p className="import-note">
          <Info size={13} />
          <span>
            {file.decoded.reason} Trennzeichen <code>{file.table.delimiter}</code>,{' '}
            {file.table.header.length} Spalten, {file.table.rows.length} Datenzeilen
            {file.table.blankRowsSkipped > 0 && ` · ${file.table.blankRowsSkipped} leere Zeilen übersprungen`}
            {file.table.raggedLines.length > 0 && ` · ${file.table.raggedLines.length} Zeilen mit abweichender Spaltenzahl`}
          </span>
        </p>
      )}

      {plan && plan.missingColumns.length > 0 && (
        <p className="import-error" role="alert">
          <AlertTriangle size={13} />
          <span>
            Diese Datei hat keine Spalte {plan.missingColumns.map((column) => <code key={column}>{column}</code>)} –
            ist es der richtige Export?
          </span>
        </p>
      )}

      {plan && salutationCodes.length > 0 && (
        <SalutationMapping
          codes={salutationCodes}
          value={salutationKinds}
          onChange={(code, next) => setSalutationKinds((current) => ({ ...current, [code]: next }))}
        />
      )}

      {plan && machineTypeValues.length > 0 && context && (
        <TypeMapping
          values={machineTypeValues}
          types={context.machineTypes}
          value={typeMapping}
          onChange={(source, typeId) => setTypeMapping((current) => {
            const next = { ...current };
            if (typeId) next[source] = typeId;
            else delete next[source];
            return next;
          })}
        />
      )}

      {plan && <PlanPreview plan={plan} />}

      {error && <p className="import-error" role="alert"><CircleAlert size={13} /> <span>{error}</span></p>}
      {result && <p className="import-done" role="status"><Check size={13} /> <span>{result}</span></p>}

      {plan && (
        <div className="import-run">
          <button type="button" className="import-run__go" disabled={busy || importable === 0} onClick={() => void run()}>
            <Upload size={14} /> {busy ? 'Übernahme läuft …' : `${importable} ${kind === 'customers' ? 'Kunden' : 'Maschinen'} übernehmen`}
          </button>
          {plan.counts.blocked > 0 && (
            <span className="import-run__note">
              {plan.counts.blocked} blockierte {plan.counts.blocked === 1 ? 'Zeile wird' : 'Zeilen werden'} nicht übernommen.
            </span>
          )}
        </div>
      )}
    </section>
  );
}

function SalutationMapping({
  codes,
  value,
  onChange,
}: {
  readonly codes: readonly { readonly code: string; readonly count: number; readonly samples: readonly string[] }[];
  readonly value: Readonly<Record<string, CustomerKind>>;
  readonly onChange: (code: string, kind: CustomerKind) => void;
}) {
  return (
    <section className="import-map">
      <h2>Anrede-Codes zuordnen</h2>
      <p>
        Arista speichert die Anrede als Zahl. Was die Zahlen bedeuten, steht nicht in der Datei –
        und raten wäre der falsche Weg, weil die Anrede später auf euren Briefen steht. Ohne
        Zuordnung wird ein Kunde als Firma angelegt; der Name stimmt dann trotzdem.
      </p>
      <ul className="import-map__list">
        {codes.map((entry) => (
          <li key={entry.code}>
            <div className="import-map__key">
              <strong>{entry.code}</strong>
              <small>{entry.count}×</small>
            </div>
            <div className="import-map__samples">{entry.samples.join(' · ')}</div>
            <div className="import-map__choice">
              {(['company', 'person'] as const).map((option) => (
                <label key={option}>
                  <input
                    type="radio"
                    name={`anrede-${entry.code}`}
                    checked={value[entry.code] === option}
                    onChange={() => onChange(entry.code, option)}
                  />
                  {option === 'company' ? 'Firma' : 'Person'}
                </label>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TypeMapping({
  values,
  types,
  value,
  onChange,
}: {
  readonly values: readonly { readonly value: string; readonly count: number; readonly samples: readonly string[] }[];
  readonly types: readonly { readonly id: string; readonly name: string }[];
  readonly value: Readonly<Record<string, string>>;
  readonly onChange: (source: string, typeId: string) => void;
}) {
  const open = values.filter((entry) => !value[entry.value]);
  const openMachines = open.reduce((sum, entry) => sum + entry.count, 0);
  const options = useMemo(
    () => types.map((type) => ({ value: type.id, label: type.name })),
    [types],
  );

  return (
    <section className="import-map">
      <h2>Gerätearten zuordnen</h2>
      <p>
        Arista führt die Geräteart als Freitext – {values.length} verschiedene Werte in dieser
        Datei. Was eindeutig passt, ist vorbelegt. Ob „Elektro Gabelstapler“ ein eigener Typ ist
        oder ein Gabelstapler mit einem Merkmal, entscheidet ihr – nicht FORQ.
        {openMachines > 0 && <> <strong>{openMachines} Maschinen sind noch nicht zugeordnet und werden nicht übernommen.</strong></>}
      </p>
      <ul className="import-map__list">
        {values.map((entry) => (
          <li key={entry.value} className={value[entry.value] ? undefined : 'is-open'}>
            <div className="import-map__key">
              <strong>{entry.value}</strong>
              <small>{entry.count}×</small>
            </div>
            <div className="import-map__samples">{entry.samples.join(' · ')}</div>
            <div className="import-map__choice is-wide">
              <SearchSelect
                options={options}
                value={value[entry.value] ?? ''}
                placeholder="Maschinentyp wählen …"
                onChange={(next) => onChange(entry.value, next)}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Bewusst gedeckelt: 2232 Zeilen im DOM machen die Seite unbenutzbar. Die Zahl steht dabei. */
const previewLimit = 100;

function PlanPreview({ plan }: { readonly plan: ImportPlan<CustomerDraft> | ImportPlan<MachineDraft> }) {
  const [onlyProblems, setOnlyProblems] = useState(false);
  const rows = useMemo(
    () => (onlyProblems ? plan.rows.filter((row) => row.issues.length > 0) : plan.rows),
    [onlyProblems, plan.rows],
  );
  const shown = rows.slice(0, previewLimit);

  return (
    <section className="import-preview">
      <header>
        <h2>Vorschau</h2>
        <div className="import-preview__counts">
          {(['create', 'update', 'blocked'] as const).map((action) => (
            <span key={action} className={`import-pill is-${action}`}>
              {plan.counts[action]} {actionLabels[action]}
            </span>
          ))}
          <label className="import-preview__filter">
            <input type="checkbox" checked={onlyProblems} onChange={(event) => setOnlyProblems(event.target.checked)} />
            Nur Zeilen mit Hinweis
          </label>
        </div>
      </header>

      {rows.length === 0 ? (
        <p className="import-empty">Keine Zeilen.</p>
      ) : (
        <div className="import-preview__wrap">
          <table className="import-table">
            <thead>
              <tr>
                <th scope="col">Zeile</th><th scope="col">Nummer</th><th scope="col">Bezeichnung</th>
                <th scope="col">Aktion</th><th scope="col">Hinweise</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => <PreviewRow key={`${row.line}`} row={row} />)}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > shown.length && (
        <p className="import-preview__more">
          {shown.length} von {rows.length} Zeilen angezeigt – übernommen werden alle nicht blockierten.
        </p>
      )}
    </section>
  );
}

function PreviewRow({ row }: { readonly row: PlannedRow<CustomerDraft | MachineDraft> }) {
  return (
    <tr className={row.action === 'blocked' ? 'is-blocked' : undefined}>
      <td>{row.line}</td>
      <td>{row.number || '–'}</td>
      <td>{row.label}</td>
      <td><span className={`import-pill is-${row.action}`}>{actionLabels[row.action]}</span></td>
      <td>
        {row.issues.length === 0 ? '–' : (
          <ul className="import-issues">
            {row.issues.map((issue, index) => (
              <li key={index} className={`is-${issue.level}`}>
                {issue.column && <code>{issue.column}</code>} {issue.message}
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}
